export type Panel =
  | "details"
  | "library"
  | "computer"
  | "settings"
  | "routine"
  | "create-group"
  | "group-settings"
  | "import-agent"
  /** A report or file read beside the chat; never restored. */
  | "file"
  | null;
export type RightPanelState = { panel: Panel; routineId?: string };

/** Panels that are safe to remember across reloads. A bot's settings are a dialog, not one. */
const durablePanels: readonly Panel[] = [
  "details",
  "library",
  "computer",
  "routine",
  "group-settings",
  null,
];

export function rightPanelStorageKey(
  userId: string,
  spaceId: string,
  kind: "bot" | "group",
  targetId: string,
): string {
  // v2: chats saved before the panel opened by default stored "closed" without anyone closing it.
  return `rakazo:right-panel:v2:${userId}:${spaceId}:${kind}:${targetId}`;
}

/** Whether this user last closed the panel, so chats they have not opened yet stay closed too. */
function rightPanelClosedKey(userId: string): string {
  return `rakazo:right-panel-closed:${userId}`;
}

/**
 * The panel a chat with no saved layout opens with: a bot's Details on screens wide enough to
 * show it beside the chat, until the user closes the panel.
 */
export function defaultRightPanel(userId: string, kind: "bot" | "group"): Panel {
  if (kind !== "bot") return null;
  try {
    if (!matchMedia("(min-width: 1024px)").matches) return null;
    return localStorage.getItem(rightPanelClosedKey(userId)) === "1" ? null : "details";
  } catch {
    return null;
  }
}

/** Remembers whether the user left the panel open or closed it, for chats without a layout. */
export function writeRightPanelClosed(userId: string, panel: Panel) {
  if (panel !== null && !durablePanels.includes(panel)) return;
  try {
    if (panel === null) localStorage.setItem(rightPanelClosedKey(userId), "1");
    else localStorage.removeItem(rightPanelClosedKey(userId));
  } catch {
    // Restricted storage must not prevent opening or closing the panel.
  }
}

export function readRightPanelState(key: string, fallback: Panel = null): RightPanelState {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return { panel: fallback };
    const value: unknown = JSON.parse(raw);
    if (
      !value ||
      typeof value !== "object" ||
      !("panel" in value) ||
      !durablePanels.includes(value.panel as Panel)
    )
      return { panel: null };
    return {
      panel: value.panel as Panel,
      ...(value.panel === "routine" &&
      "routineId" in value &&
      typeof value.routineId === "string" &&
      value.routineId
        ? { routineId: value.routineId }
        : {}),
    };
  } catch {
    return { panel: null };
  }
}

export function writeRightPanelState(key: string, panel: Panel, routineId?: string) {
  // Keep the last durable preference while create-group, an import or a bot's settings is open.
  if (
    panel === "create-group" ||
    panel === "import-agent" ||
    panel === "settings" ||
    panel === "file"
  )
    return;
  try {
    localStorage.setItem(
      key,
      JSON.stringify({ panel, ...(panel === "routine" && routineId ? { routineId } : {}) }),
    );
  } catch {
    // Restricted storage must not prevent opening or closing the panel.
  }
}
